import React from 'react';
import ReactDOM from 'react-dom';

import UpshowLogger from '../../Logger';
import ReadinessLoading, { INTRO_MS } from './ReadinessLoading';
import ReadinessBlock from './ReadinessBlock';
import VisualTestUpNext from './VisualTestUpNext';
import TestStreamBanner from './TestStreamBanner';
import ReadinessService from '../../services/readiness/ReadinessService';

let _readinessDismiss = null;
let _bannerDismiss = null;

// Mount ReadinessLoading, run all boot checks, unmount when done.
export function runReadinessLoading() {
    if (document.getElementById('readiness-loading-root')) return Promise.resolve(null);

    return new Promise((resolve) => {
        const container = document.createElement('div');
        container.id = 'readiness-loading-root';
        document.body.appendChild(container);

        let currentChecks = [];
        let isReady = false;

        function renderLoading() {
            ReactDOM.render(
                <ReadinessLoading checks={currentChecks} ready={isReady} />,
                container
            );
        }

        renderLoading(); // Initial render — shows intro splash

        // Delay check start until the intro splash finishes, so all 3 states are visible.
        setTimeout(() => {
            isReady = true;
            renderLoading(); // Transition to checking view before first onUpdate fires
            ReadinessService.runBootChecks((checks) => {
                currentChecks = checks;
                renderLoading();
            }).then((result) => {
                ReactDOM.unmountComponentAtNode(container);
                container.remove();
                resolve(result);
            }).catch((err) => {
                UpshowLogger.error('readiness', 'boot checks failed — skipping gate', err);
                ReactDOM.unmountComponentAtNode(container);
                container.remove();
                resolve(null);
            });
        }, INTRO_MS);
    });
}

let bootTestInProgress = false;

export async function runBootTest() {
    if (bootTestInProgress || document.getElementById('readiness-loading-root')) {
        UpshowLogger.log(['bootOverlay', 'runBootTest'], 'Boot test already in progress — skipping duplicate request');
        return;
    }
    bootTestInProgress = true;
    try {
        const result = await runReadinessLoading();
        if (result) await surfaceReadinessResult(result);
    } finally {
        bootTestInProgress = false;
    }
}

// Mount block overlay (At-Risk / Not-Ready) after boot checks.
// Resolves when the user dismisses. Skips (resolves immediately) if checks passed.
export function surfaceReadinessResult(result, { dismissLabel } = {}) {
    if (_readinessDismiss) _readinessDismiss();

    const existing = document.getElementById('readiness-overlay-root');
    if (existing) { ReactDOM.unmountComponentAtNode(existing); existing.remove(); }

    if (!result || result.overallStatus === 'Ready') return Promise.resolve();

    return new Promise((resolve) => {
        const container = document.createElement('div');
        container.id = 'readiness-overlay-root';
        document.body.appendChild(container);

        const onDismiss = () => {
            if (_readinessDismiss !== onDismiss) return;
            ReactDOM.unmountComponentAtNode(container);
            container.remove();
            _readinessDismiss = null;
            resolve();
        };

        _readinessDismiss = onDismiss;

        ReactDOM.render(
            <ReadinessBlock
                checks={Object.values(result.checks)}
                overallStatus={result.overallStatus}
                onDismiss={onDismiss}
                dismissLabel={dismissLabel}
            />,
            container
        );
    });
}

// Mount the small persistent banner shown while the test stream plays.
// Returns a dismiss function; also accessible via dismissTestStreamBanner().
export function showTestStreamBanner() {
    if (_bannerDismiss) _bannerDismiss(); // Remove any existing banner first

    const container = document.createElement('div');
    container.id = 'test-stream-banner-root';
    document.body.appendChild(container);

    const dismiss = () => {
        if (_bannerDismiss !== dismiss) return; // Already replaced
        ReactDOM.unmountComponentAtNode(container);
        container.remove();
        _bannerDismiss = null;
    };

    _bannerDismiss = dismiss;

    ReactDOM.render(
        <TestStreamBanner onDismiss={dismiss} />,
        container
    );
}

export function dismissTestStreamBanner() {
    if (_bannerDismiss) _bannerDismiss();
}

// Mount "Visual Test is up Next" screen.
// Resolves true if user clicks "Run Visual Test", false if they click "Skip".
export function surfaceVisualTestUpNext() {
    return new Promise((resolve) => {
        const container = document.createElement('div');
        container.id = 'visual-test-up-next-root';
        document.body.appendChild(container);

        const dismiss = (shouldRun) => {
            ReactDOM.unmountComponentAtNode(container);
            container.remove();
            resolve(shouldRun);
        };

        ReactDOM.render(
            <VisualTestUpNext
                onRun={() => dismiss(true)}
                onSkip={() => dismiss(false)}
            />,
            container
        );
    });
}
